import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import styled, { StyleSheetManager } from 'styled-components';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';

// Import global styles and theme
import GlobalStyles from './styles/GlobalStyles';
import { theme } from './styles/theme';

// Import components
import BrandHeader from './components/BrandHeader';
import Footer from './components/Footer';
import LandingPage from './components/LandingPage';
import Features from './components/Features';
import About from './components/About';
import Contact from './components/Contact';
import ManageDashboards from './components/dashboard/ManageDashboards';
import DashboardShowcase from './components/dashboard/DashboardShowcase';
import DefaultDashboardLoader from './components/dashboard/DefaultDashboardLoader';

import WorkingDashboard from './components/dashboard/WorkingDashboard';
import ViewDashboards from './components/dashboard/ViewDashboards';
import EditDashboards from './components/dashboard/EditDashboards';
import ErrorBoundary from './components/common/ErrorBoundary';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import ActivateAccount from './components/auth/ActivateAccount';
import PasswordReset, { ForgotPassword, ResetPassword } from './components/auth/PasswordReset';
import Vessels from './components/vessels/Vessels';
import VesselDetail from './components/vessels/VesselDetail';
import SignalKBrowser from './components/signalk/SignalKBrowser';
import SignalKExplorer from './components/signalk/SignalKExplorer';
import LiveBoatView from './components/liveboat/LiveBoatView';
import PowerFlowPage from './components/powerflow/PowerFlowPage';
import EngineDiagnosticsPage from './components/engines/EngineDiagnosticsPage';
import Profile from './components/profile/Profile';
import Admin from './components/admin/Admin';
import Academy from './components/academy/Academy';
import Settings from './components/settings/Settings';
import TripsPage from './components/trips/TripsPage';
import FuelLogPage from './components/fuel/FuelLogPage';
import CreateTripForm from './components/trips/CreateTripForm';
import EditTripForm from './components/trips/EditTripForm';
import TripDetailPage from './components/trips/TripDetailPage';
import SharedTripView from './components/trips/SharedTripView';
import SharedDashboardView from './components/dashboard/SharedDashboardView';
import SharedVesselView from './components/vessels/SharedVesselView';
import TripMapVisualization from './components/trips/TripMapVisualization';
import SeasonsPage from './components/seasons/SeasonsPage';
import SeasonDetailPage from './components/seasons/SeasonDetailPage';
import SeasonDashboard from './components/analytics/SeasonDashboard';
import VesselSeasonsManagement from './components/vessels/VesselSeasonsManagement';
import OnGuardTracking from './components/onguard/OnGuardTracking';
import ChartPlotter from './components/chartplotter/ChartPlotter';
import LocationPreferences from './components/friends/LocationPreferences';
import AIAssistantButton from './components/aiAssistant/AIAssistantButton';
import ChatPage from './components/aiAssistant/ChatPage';

// Import contexts
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Filter function to prevent Framer Motion props from being passed to DOM
const shouldForwardProp = (prop) => {
  const motionProps = ['initial', 'animate', 'exit', 'variants', 'transition', 'whileHover', 'whileTap', 'whileFocus', 'whileInView', 'viewport', 'layout', 'layoutId', 'drag', 'dragConstraints', 'dragElastic', 'dragMomentum', 'dragPropagation', 'dragSnapToOrigin', 'dragTransition', 'dragControls', 'onDrag', 'onDragStart', 'onDragEnd', 'onDirectionLock', 'onPan', 'onPanStart', 'onPanEnd', 'onTap', 'onTapStart', 'onTapCancel', 'onHoverStart', 'onHoverEnd', 'onFocus', 'onBlur', 'onViewportEnter', 'onViewportLeave', 'onAnimationStart', 'onAnimationComplete', 'onUpdate', 'onAnimationStart', 'onAnimationComplete', 'onUpdate', 'custom', 'inherit', 'style', 'transformTemplate', 'transformValues'];
  return !motionProps.includes(prop);
};

const AppContainer = styled.div`
  min-height: 100vh;
  min-height: 100dvh; /* iOS Safari: dynamic viewport, accounts for browser chrome */
  background: linear-gradient(135deg, ${theme.colors.background.primary} 0%, ${theme.colors.background.secondary} 100%);
  display: flex;
  flex-direction: column;
`;

const MainContent = styled.main`
  padding-top: 0;
  flex: 1;
`;

// Wrapper components to extract route params
const TripMapWrapper = () => {
  const { id } = useParams();
  return <TripMapVisualization tripId={id} />;
};

const SeasonDashboardWrapper = () => {
  const { year } = useParams();
  return <SeasonDashboard year={year} />;
};

const AppRoutes = () => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  const isChatPage = location.pathname === '/chat';
  const hasAcademy = !!user?.academy_enabled;

  return (
    <>
      {!isChatPage && <BrandHeader />}
      
      <MainContent>
        {loading ? null : (
        <Routes>
          {/* Public Routes */}
          <Route 
            path="/" 
            element={
              isAuthenticated ? 
              <DefaultDashboardLoader /> : 
              <LandingPage 
                onGetStarted={() => window.location.href = '/register'}
                onLogin={() => window.location.href = '/login'}
              />
            } 
          />
          
          <Route 
            path="/login" 
            element={
              isAuthenticated ? 
              <Navigate to="/dashboard" replace /> : 
              <Login />
            } 
          />
          
          <Route 
            path="/register" 
            element={
              isAuthenticated ? 
              <Navigate to="/dashboard" replace /> : 
              <Register />
            } 
          />
          
          <Route 
            path="/reset-password/:token" 
            element={<PasswordReset />} 
          />
          <Route 
            path="/activate/:token" 
            element={<ActivateAccount />} 
          />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/features" element={<Features />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/showcase" element={<DashboardShowcase />} />

          <Route path="/working-dashboard" element={<WorkingDashboard />} />
          
          {/* Protected Routes */}
          <Route 
            path="/dashboards" 
            element={
              isAuthenticated ? 
              <ManageDashboards /> : 
              <Navigate to="/login" replace />
            } 
          />
          
          <Route 
            path="/dashboard/create" 
            element={
              isAuthenticated ? 
              <Navigate to="/dashboards" replace /> : 
              <Navigate to="/login" replace />
            } 
          />
          
          <Route 
            path="/dashboard/:id/edit" 
            element={
              isAuthenticated ? 
              <ErrorBoundary title="Dashboard editor failed to load">
                <EditDashboards />
              </ErrorBoundary> : 
              <Navigate to="/login" replace />
            }
          />
          
          <Route 
            path="/dashboard/:id" 
            element={
              isAuthenticated ? 
              <ViewDashboards /> : 
              <Navigate to="/login" replace />
            } 
          />
          
          <Route 
            path="/dashboard" 
            element={
              isAuthenticated ? 
              <DefaultDashboardLoader /> : 
              <Navigate to="/login" replace />
            } 
          />
          
          <Route 
            path="/dashboard/manage" 
            element={
              isAuthenticated ? 
              <ManageDashboards /> : 
              <Navigate to="/login" replace />
            } 
          />
          
          
          <Route 
            path="/vessels" 
            element={
              isAuthenticated ? 
              <Vessels /> : 
              <Navigate to="/login" replace />
            } 
          />
          
          <Route 
            path="/vessels/:id" 
            element={
              isAuthenticated ? 
              <VesselDetail /> : 
              <Navigate to="/login" replace />
            } 
          />

          <Route
            path="/vessels/:vesselId/signalk/explore"
            element={
              isAuthenticated ?
              <SignalKExplorer /> :
              <Navigate to="/login" replace />
            }
          />

          <Route
            path="/vessels/:vesselId/live"
            element={
              isAuthenticated ?
              <LiveBoatView /> :
              <Navigate to="/login" replace />
            }
          />

          <Route
            path="/vessels/:vesselId/power"
            element={
              isAuthenticated ?
              <ErrorBoundary title="Power flow failed to load">
                <PowerFlowPage />
              </ErrorBoundary> :
              <Navigate to="/login" replace />
            }
          />

          <Route
            path="/power"
            element={
              isAuthenticated ?
              <ErrorBoundary title="Power flow failed to load">
                <PowerFlowPage />
              </ErrorBoundary> :
              <Navigate to="/login" replace />
            }
          />

          <Route
            path="/vessels/:vesselId/engines"
            element={
              isAuthenticated ?
              <ErrorBoundary title="Engine diagnostics failed to load">
                <EngineDiagnosticsPage />
              </ErrorBoundary> :
              <Navigate to="/login" replace />
            }
          />

          <Route
            path="/engines"
            element={
              isAuthenticated ?
              <ErrorBoundary title="Engine diagnostics failed to load">
                <EngineDiagnosticsPage />
              </ErrorBoundary> :
              <Navigate to="/login" replace />
            }
          />

          <Route 
            path="/vessels/:vesselId/seasons/view" 
            element={
              isAuthenticated ? 
              <VesselSeasonsManagement /> : 
              <Navigate to="/login" replace />
            } 
          />

          <Route 
            path="/vessels/:vesselId/seasons/:seasonId" 
            element={
              isAuthenticated ? 
              <SeasonDetailPage /> : 
              <Navigate to="/login" replace />
            } 
          />
          
          {/* OnGuard Tracking Routes - Public (no auth required for emergency access) */}
          <Route path="/onguard/track/:vesselId/:eventId" element={<OnGuardTracking />} />
          <Route 
            path="/onguard/dismiss/:vesselId/:eventId" 
            element={<OnGuardTracking />} 
          />
          
          <Route 
            path="/trips" 
            element={
              isAuthenticated ? 
              <TripsPage /> : 
              <Navigate to="/login" replace />
            } 
          />
          
          <Route 
            path="/fuel" 
            element={
              isAuthenticated ? 
              <FuelLogPage /> : 
              <Navigate to="/login" replace />
            } 
          />
          
          <Route 
            path="/trips/create" 
            element={
              isAuthenticated ? 
              <CreateTripForm /> : 
              <Navigate to="/login" replace />
            } 
          />
          
          <Route 
            path="/trips/:id/edit" 
            element={
              isAuthenticated ? 
              <EditTripForm /> : 
              <Navigate to="/login" replace />
            }
          />
          
          <Route 
            path="/trips/:id/map" 
            element={
              isAuthenticated ? 
              <TripMapWrapper /> : 
              <Navigate to="/login" replace />
            }
          />
          
          <Route 
            path="/trips/:id" 
            element={
              isAuthenticated ? 
              <ErrorBoundary title="Trip page failed to load">
                <TripDetailPage />
              </ErrorBoundary> : 
              <Navigate to="/login" replace />
            }
          />
          
          <Route 
            path="/analytics/season/:year" 
            element={
              isAuthenticated ? 
              <SeasonDashboardWrapper /> : 
              <Navigate to="/login" replace />
            }
          />

          {/* Public shared trip view - no authentication required */}
          <Route 
            path="/shared-trip/:token" 
            element={<SharedTripView />}
          />

          {/* Public shared dashboard view - no authentication required */}
          <Route
            path="/shared-dashboard/:token"
            element={<SharedDashboardView />}
          />

          {/* Public shared vessel tracking - no authentication required */}
          <Route
            path="/shared-vessel/:token"
            element={<SharedVesselView />}
          />

          <Route 
            path="/seasons" 
            element={
              isAuthenticated ? 
              <SeasonsPage /> : 
              <Navigate to="/login" replace />
            }
          />
          
          <Route 
            path="/signalk" 
            element={
              isAuthenticated ? 
              <SignalKBrowser /> : 
              <Navigate to="/login" replace />
            }
          />
          
          <Route 
            path="/profile" 
            element={
              isAuthenticated ? 
              <Profile /> : 
              <Navigate to="/login" replace />
            } 
          />
          
          

          <Route 
            path="/admin" 
            element={
              isAuthenticated ? 
              <Admin /> : 
              <Navigate to="/login" replace />
            } 
          />

          <Route
            path="/academy/*"
            element={
              isAuthenticated && hasAcademy ? (
                <Academy />
              ) : isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          
          <Route 
            path="/settings" 
            element={
              isAuthenticated ? 
              <Settings /> : 
              <Navigate to="/login" replace />
            }
          />

          <Route 
            path="/friends" 
            element={
              isAuthenticated ? 
              <Navigate to="/navigation" replace /> : 
              <Navigate to="/login" replace />
            }
          />

          <Route 
            path="/navigation" 
            element={
              isAuthenticated ? 
              <ErrorBoundary title="Vessel Tracker failed to load">
                <ChartPlotter />
              </ErrorBoundary> : 
              <Navigate to="/login" replace />
            }
          />
          
          {/* Legacy route redirect */}
          <Route 
            path="/chart-plotter" 
            element={
              isAuthenticated ? 
              <Navigate to="/navigation" replace /> : 
              <Navigate to="/login" replace />
            }
          />

          <Route 
            path="/friends/settings" 
            element={
              isAuthenticated ? 
              <LocationPreferences /> : 
              <Navigate to="/login" replace />
            }
          />

          {/* Catch-all route */}
          <Route 
            path="*" 
            element={
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <h2>Page Not Found</h2>
                <p>The page you're looking for doesn't exist.</p>
              </div>
            } 
          />
        </Routes>
        )}
      </MainContent>
      
      {!isChatPage && <Footer />}
    </>
  );
};

const App = () => {
  return (
    <HelmetProvider>
      <StyleSheetManager shouldForwardProp={shouldForwardProp}>
        <AppContainer>
          <GlobalStyles />
          <AuthProvider>
            <Router>
              <AppRoutes />
              <AIAssistantButton />
            <Toaster 
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: theme.colors.background.paper,
                  color: theme.colors.text.primary,
                  border: `1px solid ${theme.colors.border.primary}`,
                  borderRadius: theme.borderRadius.lg,
                },
                success: {
                  style: {
                    background: theme.colors.success.main,
                    color: theme.colors.success.contrast,
                  },
                },
                error: {
                  style: {
                    background: theme.colors.error.main,
                    color: theme.colors.error.contrast,
                  },
                },
              }}
            />
          </Router>
        </AuthProvider>
        </AppContainer>
      </StyleSheetManager>
    </HelmetProvider>
  );
};

export default App;